import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { LABEL_COUNTRIES, COUNTRY_CODES } from "@/constants/labelCountries";
import { Landmark, Loader2, FileDown } from "lucide-react";

const REPORT_LANGS = [
  { v: "fr", l: "Français" }, { v: "en", l: "English" }, { v: "de", l: "Deutsch" },
  { v: "es", l: "Español" }, { v: "it", l: "Italiano" }, { v: "nl", l: "Nederlands" },
];

export default function AuthorityReportDialog() {
  const { t, lang } = useLang();
  const names = LABEL_COUNTRIES[lang] || LABEL_COUNTRIES.en;
  const [open, setOpen] = useState(false);
  const [country, setCountry] = useState("");
  const [authorities, setAuthorities] = useState([]);
  const [picked, setPicked] = useState("");
  const [reportLang, setReportLang] = useState("fr");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!country) { setAuthorities([]); setPicked(""); return; }
    api.get("/authorities", { params: { country } }).then(r => {
      setAuthorities(r.data.authorities || []);
      setPicked("");
      setReportLang(r.data.report_lang || "en");
    });
  }, [country]);

  const current = authorities.find(a => a.name === picked);

  const generate = async () => {
    setLoading(true);
    try {
      const r = await api.post("/reports/authority", { country, authority: picked, lang: reportLang }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `empco-controle-${country}.pdf`; a.click();
      toast.success(t("authority.generated"));
      setOpen(false);
    } catch (e) {
      toast.error(t("authority.error"));
    } finally { setLoading(false); }
  };

  const sortedCodes = [...COUNTRY_CODES].sort((a, b) => names[a].name.localeCompare(names[b].name));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-lg h-11 border-border" data-testid="authority-report-btn">
          <Landmark className="w-4 h-4 mr-2" /> {t("authority.cta")}
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-lg max-w-lg max-h-[90vh] overflow-y-auto" data-testid="authority-report-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{t("authority.title")}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">{t("authority.intro")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2 block">{t("authority.country")}</Label>
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger className="rounded-lg h-11 w-full [&>span]:truncate [&>span]:text-left" data-testid="authority-country"><SelectValue placeholder={t("authority.country.ph")} /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="EU">{t("authority.eu")}</SelectItem>
                {sortedCodes.map(c => <SelectItem key={c} value={c}>{names[c].name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2 block">{t("authority.body")}</Label>
            <Select value={picked} onValueChange={setPicked} disabled={!country}>
              <SelectTrigger className="rounded-lg h-11 w-full [&>span]:truncate [&>span]:text-left" data-testid="authority-body"><SelectValue placeholder={country ? t("authority.body.ph") : t("authority.country.ph")} /></SelectTrigger>
              <SelectContent className="max-h-72">
                {authorities.map(a => (
                  <SelectItem key={a.name} value={a.name}>{a.acronym ? `${a.acronym} — ` : ""}{a.name.slice(0, 60)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {current && (
            <div className="rounded-lg border border-border p-3 space-y-1.5" data-testid="authority-meta">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{current.type}</p>
              <p className="text-xs">{current.role}</p>
              {current.max_sanctions && <p className="text-xs text-[hsl(var(--status-red))]">{current.max_sanctions}</p>}
              {current.official_url && <a href={current.official_url} target="_blank" rel="noreferrer" className="text-[11px] text-primary link-underline">{t("authority.site")} ↗</a>}
            </div>
          )}
          <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2 block">{t("authority.lang")}</Label>
            <Select value={reportLang} onValueChange={setReportLang}>
              <SelectTrigger className="rounded-lg h-11" data-testid="authority-lang"><SelectValue /></SelectTrigger>
              <SelectContent>{REPORT_LANGS.map(l => <SelectItem key={l.v} value={l.v}>{l.l}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1.5">{t("authority.lang.hint")}</p>
          </div>
          <DialogFooter className="mt-2">
            <Button onClick={generate} disabled={!picked || loading} className="rounded-lg bg-primary text-primary-foreground" data-testid="authority-generate">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
              {loading ? t("authority.generating") : t("authority.generate")}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
